// /////////////////////////////////////////////////////////////
// [ファイル] SalesProjectLostReasonDialog.tsx
// [概要] フェーズを「7.失注」へ変更する際の失注理由入力ダイアログ
//        理由区分は mst_sfa_lost_reason から取得し、区分選択を必須とする
// /////////////////////////////////////////////////////////////
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Typography,
  Box,
} from '@mui/material';
import * as Icons from '../constants/icons';
import {
  STANDARD_CTA_BUTTON_SX,
  STANDARD_TEXT_BUTTON_SX,
} from '../constants/buttonStyles';
import { ep } from '../api/endpoints';

export interface LostReasonValue {
  lost_reason_category: string;
  lost_reason_detail: string;
}

interface LostReasonMaster {
  reason_code: string;
  reason_name: string;
  sort_order: number;
}

interface Props {
  open: boolean;
  /** 既存値（編集時の初期表示用） */
  initialValue?: Partial<LostReasonValue>;
  onCancel: () => void;
  onConfirm: (value: LostReasonValue) => void;
}

export default function SalesProjectLostReasonDialog({ open, initialValue, onCancel, onConfirm }: Props) {
  const [reasons, setReasons] = useState<LostReasonMaster[]>([]);
  const [category, setCategory] = useState('');
  const [detail, setDetail] = useState('');

  // マスタは初回オープン時に一度だけ取得する
  useEffect(() => {
    if (!open || reasons.length > 0) return;
    axios.post(ep('get_sfa_pipeline_masters'), {})
      .then((res) => {
        if (res.data.status === 'success') setReasons(res.data.lost_reasons || []);
      })
      .catch((err) => console.error('Error fetching lost reason master:', err));
  }, [open, reasons.length]);

  useEffect(() => {
    if (open) {
      setCategory(initialValue?.lost_reason_category || '');
      setDetail(initialValue?.lost_reason_detail || '');
    }
  }, [open, initialValue]);

  return (
    <Dialog open={open} onClose={onCancel} fullWidth maxWidth="xs">
      <DialogTitle sx={{ py: 0.75, display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Icons.InfoOutlined sx={{ fontSize: 18, color: 'error.main' }} />
        <Typography component="span" sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
          失注理由の入力
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          フェーズを「7.失注」へ変更します。失注分析のため理由区分を選択してください。
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            select
            required
            label="失注理由区分"
            size="small"
            fullWidth
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            sx={{
              '& .MuiInputLabel-root': { fontSize: '13px' },
              '& .MuiSelect-select': { fontSize: '13px' },
            }}
          >
            {reasons.map((r) => (
              <MenuItem key={r.reason_code} value={r.reason_code} sx={{ fontSize: '13px' }}>
                {r.reason_name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="詳細（任意）"
            size="small"
            fullWidth
            multiline
            rows={3}
            value={detail}
            onChange={(e) => setDetail(e.target.value)}
            placeholder="経緯や競合名など、分析に役立つ補足があれば入力してください"
            sx={{
              '& .MuiInputLabel-root': { fontSize: '13px' },
              '& .MuiOutlinedInput-input': { fontSize: '13px' },
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onCancel} sx={STANDARD_TEXT_BUTTON_SX}>
          キャンセル
        </Button>
        <Button
          onClick={() => onConfirm({ lost_reason_category: category, lost_reason_detail: detail })}
          disabled={!category}
          startIcon={<Icons.Save />}
          sx={STANDARD_CTA_BUTTON_SX}
        >
          確定して保存
        </Button>
      </DialogActions>
    </Dialog>
  );
}
